/**
 * Utility for removing non-essential tracking scripts from the DOM
 * without requiring a page reload when cookie consent is rejected.
 *
 * Uses a generation counter to ensure next/script's internal LoadCache
 * doesn't prevent scripts from re-loading when the user re-consents.
 */

let scriptGeneration = 0;

export function getScriptGeneration(): number {
  return scriptGeneration;
}

export function removeTrackingScripts(): void {
  if (typeof window === 'undefined') return;
  scriptGeneration++;

  // NOTE: FullStory and New Relic are essential — NOT cleaned up here

  // Klaviyo: remove scripts + clean globals
  removeScriptsByIdPrefix('klaviyo-script');
  removeScriptsByIdPrefix('klaviyo-load-script');
  removeScriptsByIdPrefix('klaviyo-configuration');
  removeScriptsBySrcPattern('static.klaviyo.com');
  deleteGlobals(['klaviyo', '_klOnsite']);

  // GTM: remove scripts + iframe + reset dataLayer
  removeScriptsByIdPrefix('gtm-script');
  removeScriptsBySrcPattern('googletagmanager.com');
  removeScriptsBySrcPattern('google-analytics.com');
  removeScriptsBySrcPattern('googleadservices.com');
  removeScriptsBySrcPattern('doubleclick.net');
  document.querySelectorAll('iframe[src*="googletagmanager.com"]').forEach(el => el.remove());
  const win = window as unknown as Record<string, unknown>;
  if (Array.isArray(win.dataLayer)) (win.dataLayer as unknown[]).length = 0;
  deleteGlobals(['google_tag_manager']);

  // PawTree Pixel
  removeScriptsByIdPrefix('pawtree-pixel');
  removeScriptsBySrcPattern('t.pawtree.com');
}

function removeScriptsByIdPrefix(prefix: string): void {
  document.querySelectorAll(`script[id^="${prefix}"]`).forEach(el => el.remove());
}

function removeScriptsBySrcPattern(pattern: string): void {
  document.querySelectorAll(`script[src*="${pattern}"]`).forEach(el => el.remove());
}

function deleteGlobals(keys: string[]): void {
  const win = window as unknown as Record<string, unknown>;
  keys.forEach(key => {
    try {
      delete win[key];
    } catch {
      // Some properties may not be configurable
    }
    // Also explicitly set to undefined as a safety measure
    try {
      win[key] = undefined;
    } catch {
      // Ignore if property is read-only
    }
  });
}
